import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Payment as PaymentIcon,
  LocalShipping as DeliveryIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const RepairView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [repair, setRepair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [openDeliveryDialog, setOpenDeliveryDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'cash',
    reference_number: '',
    notes: ''
  });
  const [completeData, setCompleteData] = useState({
    completion_notes: '',
    warranty_months: 0
  });

  useEffect(() => {
    fetchRepair();
  }, [id]);

  const fetchRepair = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/repairs/${id}`);
      if (response.success) {
        setRepair(response.data);
        updateStepFromStatus(response.data.status);
      }
    } catch (error) {
      console.error('Error fetching repair:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStepFromStatus = (status) => {
    const statusSteps = {
      'pending': 0,
      'diagnosis': 1,
      'quoted': 2,
      'approved': 3,
      'in_progress': 4,
      'completed': 5,
      'delivered': 6,
      'cancelled': 0
    };
    setActiveStep(statusSteps[status] || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'default',
      'diagnosis': 'info',
      'quoted': 'warning',
      'approved': 'primary',
      'in_progress': 'secondary',
      'completed': 'success',
      'delivered': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'success',
      'medium': 'warning',
      'high': 'error',
      'urgent': 'error'
    };
    return colors[priority] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handlePayment = async () => {
    try {
      const response = await api.post(`/repairs/${id}/payment`, paymentData);
      if (response.success) {
        setOpenPaymentDialog(false);
        setPaymentData({
          amount: 0,
          payment_method: 'cash',
          reference_number: '',
          notes: ''
        });
        fetchRepair(); // Refresh data
      }
    } catch (error) {
      console.error('Error recording payment:', error);
    }
  };

  const handleComplete = async () => {
    try {
      const response = await api.put(`/repairs/${id}/complete`, completeData);
      if (response.success) {
        setOpenCompleteDialog(false);
        setCompleteData({
          completion_notes: '',
          warranty_months: 0
        });
        fetchRepair(); // Refresh data
      }
    } catch (error) {
      console.error('Error completing repair:', error);
    }
  };

  const handleDeliver = async () => {
    try {
      const response = await api.put(`/repairs/${id}/deliver`);
      if (response.success) {
        setOpenDeliveryDialog(false);
        fetchRepair(); // Refresh data
      }
    } catch (error) {
      console.error('Error delivering repair:', error);
    }
  };

  const steps = [
    'Pending',
    'Diagnosis',
    'Quoted',
    'Approved',
    'In Progress',
    'Completed',
    'Delivered'
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography>Loading repair details...</Typography>
      </Box>
    );
  }

  if (!repair) {
    return (
      <Box>
        <Typography variant="h5" color="error">
          Repair not found
        </Typography>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/repairs')}>
          Back to Repairs
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/repairs')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              {repair.repair_number}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {repair.device_type} - {repair.device_model}
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            startIcon={<EditIcon />}
            onClick={() => navigate(`/repairs/edit/${id}`)}
            variant="outlined"
          >
            Edit
          </Button>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            onClick={() => window.print()}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Status Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box display="flex" justifyContent="center" gap={2} mt={3}>
          <Chip 
            label={repair.status.replace('_', ' ').toUpperCase()} 
            color={getStatusColor(repair.status)}
            size="medium"
          />
          <Chip 
            label={repair.priority.toUpperCase()} 
            color={getPriorityColor(repair.priority)}
            size="medium"
            variant="outlined"
          />
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Repair Details */}
        <Grid item xs={12} md={8}>
          {/* Device Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Device Type
                  </Typography>
                  <Typography variant="body1">{repair.device_type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Model
                  </Typography>
                  <Typography variant="body1">{repair.device_model || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body1">{repair.device_serial || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Received Date
                  </Typography>
                  <Typography variant="body1">{formatDate(repair.received_date)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Issue Description
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {repair.device_issue || 'No description provided'}
                  </Typography>
                </Grid>
                {repair.diagnosis && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Diagnosis
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {repair.diagnosis}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Parts Used */}
          {repair.items && repair.items.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Parts Used
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part Name</TableCell>
                        <TableCell>Serial</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Cost</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {repair.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell>{item.serial_number || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={item.source} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>LKR {parseFloat(item.unit_cost || 0).toFixed(2)}</TableCell>
                          <TableCell>LKR {parseFloat(item.unit_price || 0).toFixed(2)}</TableCell>
                          <TableCell>LKR {parseFloat(item.line_total || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Customer & Financial Info */}
        <Grid item xs={12} md={4}>
          {/* Customer Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>{repair.customer_name}</strong>
              </Typography>
              {repair.Customer && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    Phone: {repair.Customer.phone || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Email: {repair.Customer.email || 'N/A'}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Summary
              </Typography>
              <Box mb={2}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Labor Cost:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography>LKR {parseFloat(repair.labor_cost || 0).toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">Parts Cost:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography>LKR {parseFloat(repair.parts_cost || 0).toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">Subtotal:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography>LKR {parseFloat(repair.subtotal || 0).toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">Tax ({repair.tax_rate || 0}%):</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography>LKR {parseFloat(repair.tax_amount || 0).toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">Discount:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography color="error">-LKR {parseFloat(repair.discount || 0).toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight="bold">
                      Total Amount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography variant="body1" fontWeight="bold">
                      LKR {parseFloat(repair.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2">Deposit Paid:</Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography color="success.main">
                      LKR {parseFloat(repair.deposit_paid || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight="bold">
                      Balance Due:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={repair.balance_due > 0 ? 'error' : 'success.main'}
                    >
                      LKR {parseFloat(repair.balance_due || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Action Buttons */}
              <Box display="flex" flexDirection="column" gap={1}>
                {repair.status === 'completed' && repair.balance_due > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={() => {
                      setPaymentData({
                        ...paymentData,
                        amount: repair.balance_due
                      });
                      setOpenPaymentDialog(true);
                    }}
                  >
                    Record Payment
                  </Button>
                )}
                
                {repair.status === 'in_progress' && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => setOpenCompleteDialog(true)}
                  >
                    Mark as Complete
                  </Button>
                )}
                
                {repair.status === 'completed' && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<DeliveryIcon />}
                    onClick={() => setOpenDeliveryDialog(true)}
                  >
                    Mark as Delivered
                  </Button>
                )}
                
                {repair.status === 'diagnosis' && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/repairs/${id}/quotation`)}
                  >
                    Create Quotation
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Received: {formatDate(repair.received_date)}
                </Typography>
                {repair.estimated_completion && (
                  <Typography variant="body2" color="textSecondary">
                    Estimated Completion: {formatDate(repair.estimated_completion)}
                  </Typography>
                )}
                {repair.completion_date && (
                  <Typography variant="body2" color="textSecondary">
                    Completed: {formatDate(repair.completion_date)}
                  </Typography>
                )}
                {repair.notes && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Notes: {repair.notes}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)}>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, minWidth: 300 }}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={paymentData.amount}
              onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
              InputProps={{
                startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={paymentData.payment_method}
              onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
              sx={{ mb: 2 }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Reference Number"
              value={paymentData.reference_number}
              onChange={(e) => setPaymentData({...paymentData, reference_number: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes"
              value={paymentData.notes}
              onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePayment}>
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={openCompleteDialog} onClose={() => setOpenCompleteDialog(false)}>
        <DialogTitle>Complete Repair</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, minWidth: 300 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Completion Notes"
              value={completeData.completion_notes}
              onChange={(e) => setCompleteData({...completeData, completion_notes: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Warranty (Months)"
              type="number"
              value={completeData.warranty_months}
              onChange={(e) => setCompleteData({...completeData, warranty_months: parseInt(e.target.value) || 0})}
              helperText="Enter 0 for no warranty"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompleteDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleComplete}>
            Complete Repair
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delivery Dialog */}
      <Dialog open={openDeliveryDialog} onClose={() => setOpenDeliveryDialog(false)}>
        <DialogTitle>Mark as Delivered</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark this repair as delivered?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This will change the status to "Delivered" and notify the customer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeliveryDialog(false)}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handleDeliver}>
            Mark as Delivered
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepairView;